import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useAuthStore } from '../../state/auth.store';

/**
 * A phone-only sticky CTA bar. The hero already has the big Create Room button, but once a visitor scrolls
 * past it on a small screen the primary action is out of reach until the very bottom — a real mobile
 * conversion leak. This slides up from the bottom edge ONLY after the hero has scrolled away, so it never
 * competes with the hero CTA and never covers content the visitor is actively reading at the top. It also
 * RETRACTS once the closing Final-CTA section enters view, so the visitor never sees two stacked amber
 * primaries at the very end — the page gets a clean finish.
 *
 * Hidden entirely at `md`+ (desktop keeps the always-visible nav CTA). Auth-aware label like every other CTA.
 * Respects the iOS home-indicator safe area. Motion (the slide-in) is gated behind reduced-motion → it just
 * appears. Its own `data-testid` is distinct from the hero button so e2e never hits an ambiguous match.
 */
export function MobileCtaBar(): JSX.Element {
  const reduce = useReducedMotion();
  const authed = useAuthStore((s) => s.tokens !== null);
  const [pastHero, setPastHero] = useState(false);
  const [finalInView, setFinalInView] = useState(false);

  useEffect(() => {
    // Reveal once the visitor has scrolled roughly past the hero fold.
    const onScroll = (): void => setPastHero(window.scrollY > window.innerHeight * 0.85);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Retract the bar while the closing Final-CTA section is on screen (it has its own big Create Room button).
  useEffect(() => {
    const final = document.getElementById('final-cta');
    if (!final || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => setFinalInView(entries[0]?.isIntersecting ?? false), {
      rootMargin: '0px 0px -20% 0px',
    });
    io.observe(final);
    return () => io.disconnect();
  }, []);

  const show = pastHero && !finalInView;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 md:hidden">
      <AnimatePresence>
        {show && (
          <motion.div
            initial={reduce ? false : { y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { y: 80, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-white/10 bg-[#0b1020]/85 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl"
          >
            <Link
              to={authed ? '/lobby' : '/register'}
              data-testid="cta-create-room-mobile"
              className="pointer-events-auto flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-6 py-3.5 text-base font-bold text-slate-900 shadow-[0_8px_30px_rgba(255,210,63,0.4)] outline-none transition active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-amber-200"
            >
              {authed ? 'Enter lobby' : 'Sign up — it’s free'}
              <span aria-hidden="true">→</span>
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
